/* ────────────────────────────────────────────────────────────────────────────
 * CREDIT ECONOMY — pay as you use, no subscription
 * ────────────────────────────────────────────────────────────────────────────
 * Everything is priced per SECOND OF OUTPUT, per model. That is the only model
 * that survives plugging in more engines later: each engine carries its own
 * rate, and the wallet does not care which one produced the clip.
 *
 * ── The unit ────────────────────────────────────────────────────────────────
 *   1 credit = €0.025 at the base rate  →  40 credits per €1.
 * Bulk tiers add a bonus (0 → 30%), so the effective rate improves to about
 * €0.019/credit at the top tier. Credits never expire and there is no monthly
 * fee — you only ever pay for seconds you actually render.
 *
 * ── Where the numbers come from (checked Aug 2026) ──────────────────────────
 *   Kling 3.0 API .......... ~$0.09-0.14 /s video, native audio ~9 credits/s
 *                            at 720p and 12/s at 1080p on Kling's own scale.
 *                            A 5 s 1080p clip WITH audio lands at ~$0.70,
 *                            720p with audio ~$0.56.
 *   Veo 3.1 Fast ........... ~$0.10 /s at 720p, audio included.
 *   Veo 3.1 Standard ....... ~$0.75 /s — native 4K, best lip-sync.
 *   Sora 2 ................. ~$0.30-0.50 /s (Pro tier).
 *   Runway Gen-4 Turbo ..... ~$0.05 /s.
 *   Local (Maestro/WanGP) .. runs on our own GPU — marginal cost is
 *                            electricity, so it is priced as the cheap option.
 *
 * Retail rates below sit at roughly 2x the wholesale API cost, which leaves
 * ~50-60% gross margin at the base tier and ~40-50% at the top bonus tier
 * after the bulk discount is applied.
 * ──────────────────────────────────────────────────────────────────────────── */

import type { AspectRatio, GenerationSettings, Resolution } from '../generation/types';

/** Base conversion: how many credits €1 buys before any bulk bonus. */
export const CREDITS_PER_EURO = 40;

/** Retail value of a single credit at the base rate, in euro. */
export const EURO_PER_CREDIT = 1 / CREDITS_PER_EURO;

/* ── Purchase tiers ───────────────────────────────────────────────────────── */

export interface Tier {
  euro: number;
  /** 0..1 — extra credits on top of the base conversion. */
  bonus: number;
  label: string;
}

export const TIERS: Tier[] = [
  { euro: 10, bonus: 0.0, label: 'Taster' },
  { euro: 20, bonus: 0.05, label: 'Starter' },
  { euro: 50, bonus: 0.12, label: 'Creator' },
  { euro: 100, bonus: 0.18, label: 'Studio' },
  { euro: 250, bonus: 0.25, label: 'Production' },
  { euro: 500, bonus: 0.3, label: 'Scale' },
];

export interface TierQuote {
  tier: Tier;
  baseCredits: number;
  bonusCredits: number;
  totalCredits: number;
  /** Effective €/credit — lower is better value. */
  pricePerCredit: number;
  /** How much better than the worst (€10) tier, as 0..1. */
  savingsVsBase: number;
}

export function quoteTier(tier: Tier): TierQuote {
  const baseCredits = Math.round(tier.euro * CREDITS_PER_EURO);
  const bonusCredits = Math.round(baseCredits * tier.bonus);
  const totalCredits = baseCredits + bonusCredits;
  const pricePerCredit = tier.euro / totalCredits;
  return {
    tier,
    baseCredits,
    bonusCredits,
    totalCredits,
    pricePerCredit,
    savingsVsBase: 1 - pricePerCredit / EURO_PER_CREDIT,
  };
}

/* ── Model catalogue ──────────────────────────────────────────────────────────
 * Adding an engine later means adding a row here and a provider module — the
 * wallet, the history and the cost preview all pick it up automatically.
 * ─────────────────────────────────────────────────────────────────────────── */

export type ModelId =
  | 'local-wan22'
  | 'local-ltx2'
  | 'kling-3'
  | 'veo-31-fast'
  | 'veo-31'
  | 'sora-2'
  | 'runway-gen4-turbo';

export interface ModelPricing {
  id: ModelId;
  label: string;
  vendor: string;
  /** Credits per second of output at 720p, video only. */
  creditsPerSecond: number;
  /** Extra credits per second when native audio is generated. */
  audioCreditsPerSecond: number;
  /** True once a provider module is actually wired up for it. */
  available: boolean;
  /** Shown on the model card. */
  note: string;
  /**
   * True for models that run on this machine's own GPU rather than a hosted
   * API. Local models are billed for GPU time, not a fixed per-second API
   * rate, so their cost also scales with the step count.
   */
  isLocal?: boolean;
  /** Maestro model_type this maps to. Local models only. */
  maestroModelType?: string;
}

export const MODELS: ModelPricing[] = [
  {
    id: 'local-wan22',
    label: 'Wan 2.2 FastWan 5B',
    vendor: 'Local · Maestro',
    creditsPerSecond: 1,
    audioCreditsPerSecond: 0,
    available: true,
    isLocal: true,
    maestroModelType: 'ti2v_2_2_fastwan',
    note: 'Fastest local option. Distilled 3-step 5B model — quick, but visibly softer quality.',
  },
  {
    id: 'local-ltx2',
    label: 'LTX-2.3 Distilled 22B (Q4)',
    vendor: 'Local · Maestro',
    creditsPerSecond: 3,
    audioCreditsPerSecond: 0,
    available: true,
    isLocal: true,
    maestroModelType: 'ltx2_22B_distilled_gguf_q4_k_m',
    note: 'Lightricks LTX-2.3, GGUF-quantized for tight VRAM. Noticeably higher quality than FastWan 5B, still fast (8 steps). Always renders a native soundtrack too — needs ffmpeg on PATH (winget install Gyan.FFmpeg), otherwise the final mux step fails.',
  },
  {
    id: 'kling-3',
    label: 'Kling 3.0',
    vendor: 'Kuaishou',
    creditsPerSecond: 7,
    audioCreditsPerSecond: 4,
    available: false,
    note: 'Strong motion, native audio. ~€1.65 for 5 s at 1080p with sound.',
  },
  {
    id: 'veo-31-fast',
    label: 'Veo 3.1 Fast',
    vendor: 'Google',
    creditsPerSecond: 8,
    audioCreditsPerSecond: 0,
    available: false,
    note: 'Audio included in the base rate. Good speed/quality balance.',
  },
  {
    id: 'veo-31',
    label: 'Veo 3.1 Standard',
    vendor: 'Google',
    creditsPerSecond: 60,
    audioCreditsPerSecond: 0,
    available: false,
    note: 'Native 4K and the best lip-sync available. Premium rate.',
  },
  {
    id: 'sora-2',
    label: 'Sora 2 Pro',
    vendor: 'OpenAI',
    creditsPerSecond: 32,
    audioCreditsPerSecond: 0,
    available: false,
    note: 'Excellent physics and scene coherence.',
  },
  {
    id: 'runway-gen4-turbo',
    label: 'Gen-4 Turbo',
    vendor: 'Runway',
    creditsPerSecond: 4,
    audioCreditsPerSecond: 0,
    available: false,
    note: 'Fast and inexpensive. Video only.',
  },
];

export const DEFAULT_MODEL_ID: ModelId = 'local-wan22';

export const getModel = (id: ModelId): ModelPricing =>
  MODELS.find((m) => m.id === id) ?? MODELS[0];

/* ── Generation cost ──────────────────────────────────────────────────────── */

/** Clip length the engine currently produces, in seconds. */
export const CLIP_SECONDS = 5;

const RESOLUTION_MULTIPLIER: Record<Resolution, number> = {
  '480p': 0.6,
  '720p': 1,
  '1080p': 1.35,
};

export const STEP_RANGE = { min: 10, max: 50, step: 2, reference: 30 } as const;

/**
 * What one generation costs.
 * Rendering effort scales with resolution and — for the local engine, where we
 * pay in GPU time rather than API fees — with the step count too.
 */
export function costOf(settings: GenerationSettings): number {
  const model = getModel(settings.modelId);
  const res = RESOLUTION_MULTIPLIER[settings.resolution] ?? 1;
  // Hosted APIs bill per second regardless of how we set steps; only local
  // compute actually gets more expensive when you ask for more steps.
  const stepFactor = model.isLocal ? settings.steps / STEP_RANGE.reference : 1;

  const perSecond = model.creditsPerSecond * res + model.audioCreditsPerSecond;
  return Math.max(1, Math.ceil(perSecond * CLIP_SECONDS * stepFactor));
}

/** Retail euro value of a credit amount, at the base rate. */
export const creditsToEuro = (credits: number) => credits * EURO_PER_CREDIT;

/** How many clips a credit balance buys at the given settings. */
export function clipsAffordable(credits: number, settings: GenerationSettings): number {
  return Math.floor(credits / costOf(settings));
}

/** Settings used for the "≈ N clips" estimate on the purchase screen. */
export const REFERENCE_SETTINGS: GenerationSettings = {
  resolution: '720p',
  aspectRatio: 'landscape',
  steps: STEP_RANGE.reference,
  cacheEnabled: true,
  modelId: DEFAULT_MODEL_ID,
};

export const RESOLUTIONS: { value: Resolution; label: string; detail: string }[] = [
  { value: '480p', label: '480p', detail: 'draft' },
  { value: '720p', label: '720p', detail: 'balanced' },
  { value: '1080p', label: '1080p', detail: 'best' },
];

export const ASPECT_RATIOS: {
  value: AspectRatio;
  label: string;
  ratio: string;
  hint: string;
}[] = [
  { value: 'landscape', label: 'Landscape', ratio: '16:9', hint: 'YouTube, web' },
  { value: 'portrait', label: 'Portrait', ratio: '9:16', hint: 'Reels, TikTok, Shorts' },
];

/** Pixel dimensions for a resolution + orientation pair. */
export function dimensionsFor(
  resolution: Resolution,
  aspect: AspectRatio,
): { width: number; height: number } {
  const base: Record<Resolution, { width: number; height: number }> = {
    '480p': { width: 832, height: 480 },
    '720p': { width: 1280, height: 720 },
    '1080p': { width: 1920, height: 1088 },
  };
  const d = base[resolution] ?? base['720p'];
  return aspect === 'portrait' ? { width: d.height, height: d.width } : d;
}
